import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, InfoNote } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, X, ChevronRight } from "lucide-react";

const STATE_CLS = {
  IMPLEMENTED: "bg-emerald-50 text-emerald-700 border-emerald-300",
  SIMULATED: "bg-sky-50 text-sky-700 border-sky-300",
  "FUTURE INTEGRATION": "bg-slate-100 text-slate-600 border-slate-300",
  "TO BE CONFIRMED": "bg-amber-50 text-amber-800 border-amber-300",
  "REQUIRES VALIDATION": "bg-amber-50 text-amber-800 border-amber-300",
};

function StateTag({ state }) {
  return <span className={`rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STATE_CLS[state] || "bg-slate-100 text-slate-600 border-slate-300"}`}>{state}</span>;
}

const CONN_CLS = (c) =>
  c === "IMPLEMENTED" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
    : c === "NOT CONNECTED" ? "border-rose-200 bg-rose-50 text-rose-700"
      : "border-amber-200 bg-amber-50 text-amber-800";

function FlowStrip({ flow, prefix }) {
  return (
    <div className="flex items-stretch gap-1 overflow-x-auto">
      {flow.map((n, i) => (
        <React.Fragment key={prefix + n.id + i}>
          <div className={`flex min-w-[140px] flex-1 flex-col rounded-md border p-2.5 ${n.connection === "IMPLEMENTED" ? "border-teal-300 bg-teal-50/40" : "border-slate-200 bg-white"}`} data-testid={`${prefix}-flow-${n.id}`}>
            <span className="text-xs font-bold text-slate-900">{n.label}</span>
            <span className="text-[10px] text-slate-500">{n.sub}</span>
            <span className="mt-1 text-[9px] uppercase tracking-wide text-slate-400">{n.identity}</span>
            <span className={`mt-1.5 inline-flex w-fit rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase ${CONN_CLS(n.connection)}`}>{n.connection}</span>
          </div>
          {i < flow.length - 1 && <div className="flex items-center"><ChevronRight className="h-4 w-4 text-slate-300" /></div>}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function OperatingModel() {
  const [model, setModel] = useState(null);
  const [wf, setWf] = useState(null);
  useEffect(() => {
    api.operatingModel().then(setModel).catch(() => {});
    api.workflowStages().then(setWf).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader testid="operating-model-header" title="RequiSure Operating Model"
        subtitle="How RequiSure coordinates intelligence and readiness across the provider EMR, laboratory, and revenue cycle — without replacing any of them." />
      <InfoNote className="mb-4">RequiSure is not the EMR/EHR, LIS, billing/RCM platform, clearinghouse, or payer system. It is a reimbursement-readiness intelligence and workflow-control layer connecting them.</InfoNote>

      <Tabs defaultValue="model">
        <TabsList className="mb-4">
          <TabsTrigger value="model" data-testid="om-tab-model">System-of-Record & Responsibility</TabsTrigger>
          <TabsTrigger value="workflow" data-testid="om-tab-workflow">End-to-End Workflow</TabsTrigger>
        </TabsList>

        <TabsContent value="model">
          {model?.flow_current && (
            <Card className="mb-4 p-4" data-testid="om-flow-current">
              <div className="mb-1 flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-800">
                Current / First-Pilot Environment
                <span className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-emerald-700">Confirmed identities</span>
              </div>
              <p className="mb-3 text-xs text-slate-500">
                Provider / Order Source (<span className="font-semibold text-amber-700">To Be Confirmed</span>) → RequiSure → <span className="font-semibold text-slate-700">CrelioHealth</span> (current & first-pilot LIS) → <span className="font-semibold text-slate-700">PayMedics</span> / RCM → Payer Outcome. Every external platform remains <span className="font-semibold text-rose-600">NOT CONNECTED</span>.
              </p>
              <FlowStrip flow={model.flow_current} prefix="cur" />
            </Card>
          )}
          {model?.flow_future && (
            <Card className="mb-4 p-4" data-testid="om-flow-future">
              <div className="mb-1 flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-800">
                Future / Target Environment
                <span className="rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-slate-500">Conceptual · not committed</span>
              </div>
              <p className="mb-3 text-xs text-slate-500">
                Potential Provider / <span className="font-semibold text-slate-700">AdvanceMD</span> → RequiSure → <span className="font-semibold text-slate-700">INVOPAD</span> or another LIS → PayMedics / RCM → Payer Outcome → RequiSure. Illustrative only — no APIs, no commitments; every platform remains <span className="font-semibold text-rose-600">NOT CONNECTED</span>.
              </p>
              <FlowStrip flow={model.flow_future} prefix="fut" />
            </Card>
          )}
          <div className="grid gap-3 lg:grid-cols-4 md:grid-cols-2">
            {model?.columns?.map((c) => (
              <Card key={c.id} className={`p-4 ${c.id === "requisure" ? "border-teal-300 bg-teal-50/30" : ""}`} data-testid={`om-col-${c.id}`}>
                <div className="mb-1 font-display text-sm font-bold text-slate-900">{c.name}</div>
                <div className="mb-3 text-[11px] text-slate-500">System: {c.system}</div>
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Owns</div>
                <ul className="mb-3 space-y-1">
                  {c.owns.map((o) => <li key={o} className="flex items-start gap-1.5 text-xs text-slate-700"><Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" /> {o}</li>)}
                </ul>
                {c.must_not_own?.length > 0 && <>
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Must not own</div>
                  <ul className="space-y-1">
                    {c.must_not_own.map((o) => <li key={o} className="flex items-start gap-1.5 text-xs text-slate-500"><X className="mt-0.5 h-3 w-3 shrink-0 text-rose-500" /> {o}</li>)}
                  </ul>
                </>}
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="workflow">
          <div className="mb-3 flex flex-wrap gap-2">
            {wf?.labels?.map((l) => <StateTag key={l} state={l} />)}
          </div>
          <div className="space-y-2">
            {wf?.stages?.map((s) => (
              <Card key={s.n} className="flex items-center gap-4 p-3" data-testid={`wf-stage-${s.n}`}>
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-slate-900 font-display text-sm font-bold text-white">{s.n}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-800">{s.title}</div>
                  <div className="text-xs text-slate-500">{s.desc}</div>
                </div>
                <StateTag state={s.state} />
                <ChevronRight className="hidden h-4 w-4 text-slate-300 sm:block" />
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
